// Adapts the synchronous /dqr aggregate into the per-section prop shapes the DQR
// components consume, plus the calcData-input object the embedded DC engine needs.
// /dqr replaces the /raw job-poll: there is no job/token/progress here.

import { SECTION } from '@aytm/app/stats/page/data-quality-report/lib/sections'
import { TIER, tierFromWire } from '@aytm/app/stats/page/data-quality-report/lib/tiers'

// performance.questionDropoffs -> the SurveyPerformance dropOff chart shape.
// /dqr returns long-unfinished drop-offs for main survey questions only (PQ / trait rows and
// pre-negative screen-outs are excluded server-side), one row per question with >= 1 drop-off,
// ordered by position. questionText arrives pre-cleaned of aytm logic. The merge below is now
// defensive — the producer no longer emits duplicate (question, status) rows.
const axisLabel = q => (q.questionPosition == null ? String(q.questionId) : `Q${q.questionPosition}`)

const toDropOff = (questionDropoffs = {}) => {
  const byQuestion = new Map()
  ;(questionDropoffs.questions || []).forEach(q => {
    const merged = byQuestion.get(q.questionId)
    if (merged) {
      merged.dropOffCount += q.dropOffCount || 0
      merged.isHighDropoff ||= Boolean(q.isHighDropoff)
    } else {
      byQuestion.set(q.questionId, {
        questionId: q.questionId,
        questionPosition: q.questionPosition,
        questionText: q.questionText ?? null,
        dropOffCount: q.dropOffCount || 0,
        isHighDropoff: Boolean(q.isHighDropoff)
      })
    }
  })

  const questions = [...byQuestion.values()].sort((a, b) => (a.questionPosition ?? 0) - (b.questionPosition ?? 0))
  // isHighDropoff was dropped from the /dqr contract (no PRD basis) and the BE never sends it now,
  // so this resolves to null and nothing is highlighted; the machinery stays for when it returns.
  const highlightIndex = questions.findIndex(q => q.isHighDropoff)
  // Respondents, not rates: the axis and the callout both count people, so a plotted value is the
  // raw dropOffCount. dropOffRate stays on the wire unconsumed — the section states the survey-level
  // rate, which it derives from the completion rate instead.
  const series = questions.map(q => q.dropOffCount)
  return {
    // The internal question id identifies nothing to an author, so the axis carries the question's
    // position and the tooltip its wording; the id is only the last-resort fallback.
    categories: questions.map(axisLabel),
    titles: questions.map(q => q.questionText || axisLabel(q)),
    series,
    highlightIndex: highlightIndex === -1 ? null : highlightIndex,
    // The producer's own total leads the section; summing the plotted rows only covers a payload
    // that omits it.
    total: questionDropoffs.totalDropoffs ?? series.reduce((sum, n) => sum + n, 0)
  }
}

const toHumanReview = humanReview => {
  // PRD Part E. aytm expert oversight = DC manual rejects + admin open-ended rejects. The client's
  // own line is their open-ended rejections only. `manuallyRejected` is the panel-level `rejected`
  // status (set by poll / GDPR / distributor plumbing, never by a human reviewer) and belongs to
  // neither set — using it here is what made the hide rule and both headings wrong.
  const expertRemovedCount = (humanReview.dcRejected || 0) + (humanReview.oeAdminRejected || 0)
  const clientRejectedCount = humanReview.oeRejected || 0
  // The hide rule is the producer's: while it fires, the counts do not reach the browser at all, so
  // their presence IS the decision and nothing is re-derived here. T0 always receives them, which
  // is what keeps the block on the investigation path the rule defers to.
  const showExpertReview = null != humanReview.dcRejected || null != humanReview.oeAdminRejected

  return {
    clientRejectedCount,
    expertRemovedCount,
    showExpertReview,
    kudosCount: humanReview.kudosCount || 0,
    // Human oversight only: expert removals + the client's own OE rejections. DC auto-quarantines
    // are the Data Centrifuge section's number, not Human Review's (PRD Part A section 4 / Part E).
    totalRejectedCount: expertRemovedCount + clientRejectedCount,
    // What the rendered blocks add up to. The hide rule suppresses the expert-removal messaging, so
    // a summary that still counted it would state a total the visible body contradicts.
    visibleRejectedCount: clientRejectedCount + (showExpertReview ? expertRemovedCount : 0)
  }
}

const toPerformance = performance => {
  const dropOff = toDropOff(performance.questionDropoffs)

  return {
    completionRate: performance.completionRate,
    // The section and its subtitle both lead with how many respondents dropped, so the count travels
    // beside the rate rather than being dug out of the chart shape at each render site.
    dropOffCount: dropOff.total,
    // Whether the count and rate above are worth stating. Only a fully placed drop-off population
    // qualifies: partly placed understates both, and a payload predating the reading cannot vouch for
    // either — an understated figure is a worse answer than none, so both withhold (§3.9).
    dropOffPlaced: 'full' === performance.dropOffAttribution,
    // respondentFeedback is a { mean, total } aggregate; the mean is withheld (null) below the ratings
    // floor. Unwrap to the scalar mean the section renders — the object itself would NaN the stars.
    positiveFeedbackRate: performance.respondentFeedback?.mean ?? null,
    // The rating count behind the mean. Lets the section tell a too-few-ratings sample (count below the
    // producer's floor, mean withheld) apart from genuine no-data, and drives the "N ratings" figure.
    positiveFeedbackCount: performance.respondentFeedback?.total ?? 0,
    // A survey that never asked and one nobody has rated yet both carry zero ratings; only this
    // separates them. Absent on payloads predating the flag, which are read as having asked.
    positiveFeedbackEnabled: performance.respondentFeedback?.enabled ?? true,
    // Absent (payload predates the sweep) or floor-gated null both resolve null, which the section
    // renders as the withheld em dash — so a benchmark the sweep has not accumulated yet shows as
    // unavailable rather than as a figure.
    platformAvgCompletion: performance.benchmarks?.platformAvgCompletion ?? null,
    platformAvgFeedback: performance.benchmarks?.platformAvgFeedback ?? null,
    dropOff
  }
}

// The DC engine's calcData reads cs[].response.uid + cs[].extra.dataCleaning and a few
// top-level scalars. /dqr ships cs rows in that family plus dataCentrifuge aggregates.
// NOTE: calcData's `dcQuarantine` is the survey's auto-quarantine *toggle* (boolean), not a
// count — /dqr carries no such flag, so we omit it here (the gate defaults off). If DC needs
// the real toggle, source it from survey config at the controller layer, not from quarantineCount.
const toDcData = (cs, dataCentrifuge) => ({
  cs: cs || [],
  dcLastRunTs: dataCentrifuge.dcLastRunTs ?? null,
  dcAnalyzedCount: dataCentrifuge.analyzedCount ?? 0,
  notAnalyzedCount: dataCentrifuge.notAnalyzedCount ?? 0,
  // The per-vector figures below the forensic tiers: no per-respondent rows arrive there, so the
  // score walk has nothing to count and this aggregate is the only figure source the rows have.
  // Null rather than {} — a tier that is sent no map at all and a run that flagged nothing must
  // stay distinguishable, since only the first has figures it is owed.
  vectorBreakdown: dataCentrifuge.vectorBreakdown ?? null,
  // Display names of the primaries this run flagged. The least capable tier renders this list
  // instead of the whole taxonomy, so an absent one must read as "not spoken about yet" and not as
  // "flagged nothing" — null keeps the two apart while the report is still in flight.
  primaryVectors: dataCentrifuge.primaryVectors ?? null,
  // drives the sidebar status popup, RUN AGAIN, the error label and the ran-clean rule
  dcStatuses: dataCentrifuge.dcStatuses ?? {}
})

export const mapDqrReport = response => {
  const { tier, surveyStatus, panel, trafficSentinel, dataCentrifuge, humanReview, performance, cs } = response
  // Fails closed twice: a non-integer wire tier never resolves, and an out-of-range integer
  // (which `tierFromWire` answers with undefined) falls back to the least capable tier.
  const resolvedTier = (Number.isInteger(tier) ? tierFromWire(tier) : null) ?? TIER.T3_LITE

  return {
    status: response.status,
    tier: resolvedTier,
    surveyStatus,
    // Keyed by the section each flag gates (mirrors the SECTION constants):
    // active once the survey has produced that section's data, greyed until then.
    fielding: {
      dataCentrifuge: Boolean(dataCentrifuge),
      humanReview: Boolean(humanReview),
      surveyPerformance: Boolean(performance)
    },
    sections: {
      [SECTION.PAID_VIEWPOINT]: panel,
      [SECTION.TRAFFIC_SENTINEL]: trafficSentinel,
      // Tier-gated aggregate (null until fielded); the DC body renders from the embedded engine,
      // this feeds the section subtitle (quarantine/analyzed counts).
      [SECTION.DATA_CENTRIFUGE]: dataCentrifuge,
      [SECTION.HUMAN_REVIEW]: humanReview ? toHumanReview(humanReview) : {},
      [SECTION.SURVEY_PERFORMANCE]: performance ? toPerformance(performance) : {}
    },
    dc: dataCentrifuge ? { data: toDcData(cs, dataCentrifuge) } : null
  }
}
